/**
 * Servicio API centralizado
 * Maneja todas las llamadas HTTP de la aplicación con configuración,
 * interceptores y manejo de errores consistente.
 */
import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import config from '../config/config';
import type { Comic, Opinion, DuplicateCheckResponse } from '../types';

// Crear instancia de Axios con configuración base
const api: AxiosInstance = axios.create({
    baseURL: config.backendUrl,
    timeout: 30000,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Interceptor de request - agregar headers comunes
api.interceptors.request.use(
    (requestConfig: InternalAxiosRequestConfig) => {
        // Aquí se pueden agregar tokens de autenticación si es necesario
        // requestConfig.headers.Authorization = `Bearer ${token}`;
        return requestConfig;
    },
    (error: AxiosError) => {
        console.error('Error en la solicitud:', error);
        return Promise.reject(error);
    }
);

// Interceptor de response - manejo centralizado de errores
api.interceptors.response.use(
    (response) => response,
    (error: AxiosError<{ message?: string; errors?: string[] }>) => {
        // Manejo de errores comunes
        if (error.response) {
            // El servidor respondió con un código de error
            const { status, data } = error.response;

            switch (status) {
                case 400:
                    console.error('Error de validación:', data?.errors || data?.message);
                    break;
                case 401:
                    console.error('No autorizado');
                    break;
                case 404:
                    console.error('Recurso no encontrado');
                    break;
                case 429:
                    console.error('Demasiadas solicitudes. Intenta más tarde.');
                    break;
                case 500:
                    console.error('Error del servidor');
                    break;
                default:
                    console.error('Error:', data?.message || 'Error desconocido');
            }
        } else if (error.request) {
            // No se recibió respuesta
            console.error('No se pudo conectar con el servidor');
        } else {
            // Error en la configuración de la solicitud
            console.error('Error:', error.message);
        }

        return Promise.reject(error);
    }
);

// ============================================
// API de Comics
// ============================================

/**
 * Obtiene todos los cómics disponibles
 */
export const getComics = async (): Promise<Comic[]> => {
    const response = await api.get<Comic[]>('/comics');
    return response.data;
};

/**
 * Obtiene un cómic por ID
 */
export const getComicById = async (id: string): Promise<Comic> => {
    const response = await api.get<Comic>(`/comics/${id}`);
    return response.data;
};

// ============================================
// API de Opiniones
// ============================================

/**
 * Envía una opinión
 */
export const submitOpinion = async (opinionData: Partial<Opinion>, fromSorteo = false): Promise<{ message: string }> => {
    const response = await api.post<{ message: string }>('/opinions/submit', {
        ...opinionData,
        fromSorteo,
    });
    return response.data;
};

/**
 * Verifica si ya existe una opinión duplicada
 */
export const checkDuplicateOpinion = async (email: string, capitulo: string): Promise<boolean> => {
    const response = await api.get<DuplicateCheckResponse>('/opinions/check-duplicate', {
        params: { email, capitulo },
    });
    return response.data.duplicate;
};

// ============================================
// API de Notificaciones/Suscripciones
// ============================================

interface SubscriptionData {
    endpoint?: string;
    keys?: { p256dh: string; auth: string };
    email?: string;
}

/**
 * Suscribe a notificaciones push o email
 */
export const subscribe = async (subscriptionData: SubscriptionData): Promise<{ message: string }> => {
    const response = await api.post<{ message: string }>('/notifications/subscribe', subscriptionData);
    return response.data;
};

/**
 * Cancela la suscripción
 */
export const unsubscribe = async (email: string): Promise<{ message: string }> => {
    const response = await api.get<{ message: string }>('/unsubscribe', {
        params: { email },
    });
    return response.data;
};

// Exportar la instancia de API para casos especiales
export default api;
